'use client';

import { Button } from '@florencelabs/button';
import { MaterialSymbol } from '@florencelabs/icons';
import { DateValue } from '@internationalized/date';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useMemo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import {
  NearestAvailability,
  getNearestAvailability,
  filterDatesByFirstStartDate,
  getOpenSlotCounts,
  NearestSlot,
} from '@provider-directory/components/availability/availability.model';
import { DaySlotsPicker } from '@provider-directory/components/availability/day-slot-picker/day-slot-picker.component';
import {
  NoSlotsSearchDesktop,
  NoSlotsSearchMobile,
} from '@provider-directory/components/availability/no-slots-actions.component';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import {
  SlotDateMode,
  useAppointment,
} from '@provider-directory/contexts/AppointmentContext';
import { useCachedSlots } from '@provider-directory/contexts/CachedSlotsContext';
import {
  useDecisionTree,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import {
  formatDateString,
  tzFreeDate,
} from '@provider-directory/functions/date';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import useMyChartRedirect from '@provider-directory/hooks/useMyChartRedirect';
import { useSchedulingDecision } from '@provider-directory/hooks/useSpecialityScheduling';
import { isSchedulingMyChart } from '@provider-directory/models/provider.model';
import type {
  ProviderLocationResource,
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';

export type SchedulingContext = 'card' | 'dropdown';

export interface ProviderCardSchedulingProps {
  provider: ProviderResource;
  location: ProviderLocationResource;
  startDate?: DateValue | null;
  endDate?: DateValue | null;
  searchCriteria?: Partial<ProvidersSearchCriteria>;
  showBookAnotherProviderButton?: boolean;
  schedulingContext?: SchedulingContext;
}

export interface SchedulingProps extends ProviderCardSchedulingProps {
  openScheduling: (
    slot: Partial<NearestSlot> | null,
    showCalendarByDefault: boolean,
  ) => void;
  nearestAvailability: NearestAvailability | null;
}

export const SchedulingDesktop = ({
  provider,
  location,
  openScheduling,
  nearestAvailability,
  startDate,
  endDate,
  searchCriteria,
  showBookAnotherProviderButton = true,
  schedulingContext = 'card',
}: SchedulingProps): ReactElement => {
  const { t } = useTranslation();
  const {
    config: { decisionTree },
  } = useOrgContext();
  const { isLoading, cachedSlots } = useCachedSlots();
  const { featureFlags } = useFeatureFlags();

  const locationEhrExternalId =
    location.ehrExternalId ?? 'UNDEFINED_LOCATION_EHR_EXTERNAL_ID';
  const openSlots =
    useMemo(
      () => getOpenSlotCounts(cachedSlots, locationEhrExternalId),
      [cachedSlots, locationEhrExternalId],
    ) ?? [];

  const hasSlots = !isLoading && openSlots.length > 0;
  const schedulingMode = useSchedulingDecision(provider, hasSlots);

  const isMyChart = isSchedulingMyChart({
    specialtyMap: decisionTree.specialtyMap,
    provider,
    featureFlags,
  });

  let startRange: Date | undefined;
  if (startDate) {
    startRange = tzFreeDate(startDate.toString());
  } else if (openSlots[0]) {
    startRange = tzFreeDate(openSlots[0].date);
  }
  const endRange = endDate ? tzFreeDate(endDate.toString()) : null;

  const openDate = (date?: string) => {
    const closestSlotDateByLocation =
      nearestAvailability?.byLocation[locationEhrExternalId]?.date;

    const slotDate =
      date ?? closestSlotDateByLocation ?? format(new Date(), 'yyyy-MM-dd');

    const slot: Partial<NearestSlot> = {
      slotsCount: 0,
      date: slotDate,
      locationId: locationEhrExternalId,
    };
    openScheduling(slot, !date);
  };

  // Sort slots chronologically to ensure correct date range logic
  const sortedSlots = [...openSlots].sort(
    (a, b) => tzFreeDate(a.date).getTime() - tzFreeDate(b.date).getTime(),
  );

  const firstSlotAfterStart = sortedSlots.find((slot) => {
    const slotDate = tzFreeDate(slot.date);
    return !startRange || slotDate >= startRange;
  });

  if (
    ['call', 'call+book'].includes(schedulingMode) ||
    (endRange && !firstSlotAfterStart)
  ) {
    return (
      <NoSlotsSearchDesktop
        provider={provider}
        location={location}
        schedulingBehavior={schedulingMode}
        onOpenDecisionTree={() => {
          openScheduling(null, false);
        }}
        searchCriteria={searchCriteria}
        linkContextPage={'SearchResults'}
        linkContextComponent={'ProviderCard'}
        showBookAnotherProviderButton={showBookAnotherProviderButton}
      />
    );
  }

  // When no endRange is provided, treat all slots as in-range (default behavior)
  // When endRange exists, check if the first slot after start is within the range
  const hasInRange = !endRange
    ? true
    : firstSlotAfterStart?.date
      ? tzFreeDate(firstSlotAfterStart.date) <= endRange
      : false;

  return (
    <div
      data-testid={'scheduling-desktop-container'}
      className={clsx(
        'flex w-full flex-col gap-4',
        // Center the buttons when there are no slots within the selected date range
        !hasInRange && 'justify-center',
      )}
    >
      <DaySlotsPicker
        isLoading={isLoading}
        daySlots={openSlots}
        startDate={startRange}
        endDate={endRange}
        showGap={true}
        onChange={openDate}
        value={null}
        providerName={provider.name.full}
      />
      {schedulingContext === 'card' && (
        <Button
          variant={'clientLink'}
          testId={`provider-${provider.npi}-view-all-availability-${isMyChart ? 'mychart-' : ''}btn`}
          className={clsx(
            'align-center ml-auto flex gap-2 py-[10px] pr-1',
            'text-display-2xsmall font-semibold text-primary-color',
            isLoading && 'invisible', // fixes jumpy card height on initial load
          )}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            openDate();
          }}
        >
          {t('details.viewAllAvailability')}
          <MaterialSymbol name={'arrow_right_alt'} size={20} />
        </Button>
      )}
    </div>
  );
};

export const SchedulingMobile = ({
  provider,
  location,
  openScheduling,
  nearestAvailability,
  searchCriteria,
  showBookAnotherProviderButton = true,
}: SchedulingProps): ReactElement => {
  const { t } = useTranslation();
  const {
    config: { decisionTree },
  } = useOrgContext();

  const { isLoading } = useCachedSlots();
  const { featureFlags } = useFeatureFlags();

  const hasNearest = Boolean(
    nearestAvailability?.byLocation[location.ehrExternalId ?? ''],
  );
  const schedulingMode = useSchedulingDecision(provider, hasNearest);

  if (isLoading) {
    return (
      <Button
        variant={'clientTonal'}
        className={'relative w-full !py-2.5 !text-[14px]'}
        isLoading={true}
      />
    );
  }

  if (['call', 'call+book'].includes(schedulingMode)) {
    return (
      <NoSlotsSearchMobile
        provider={provider}
        location={location}
        schedulingBehavior={schedulingMode}
        onOpenDecisionTree={() => {
          openScheduling(null, false);
        }}
        searchCriteria={searchCriteria}
        linkContextPage={'SearchResults'}
        linkContextComponent={'ProviderCard'}
        showBookAnotherProviderButton={showBookAnotherProviderButton}
      />
    );
  }

  const isMyChart = isSchedulingMyChart({
    provider,
    featureFlags,
    specialtyMap: decisionTree.specialtyMap,
  });
  let nearestSlot: NearestSlot | null = null;

  const nearestByLocation =
    nearestAvailability?.byLocation[location.ehrExternalId ?? ''];
  nearestSlot = nearestByLocation ?? null;

  if (nearestSlot) {
    return (
      <Button
        testId={`mobile-provider-${provider.npi}-nearest-appointments-${isMyChart ? 'mychart-' : ''}btn`}
        variant={'clientTonal'}
        onClick={(e) => {
          e.preventDefault();
          openScheduling(nearestSlot, true);
        }}
        className={
          'relative !h-[unset] w-full !text-body-small-semibold !leading-[140%] !tracking-[-0.28px]'
        }
        isLoading={isLoading}
      >
        {t('search.nApptsAvailableOn', {
          count: nearestSlot.slotsCount,
          date: formatDateString(nearestSlot.date, 'MMM d'),
        })}
      </Button>
    );
  }

  return (
    <NoSlotsSearchMobile
      provider={provider}
      location={location}
      schedulingBehavior={'call'}
      linkContextPage={'SearchResults'}
      linkContextComponent={'ProviderCard'}
    />
  );
};

export const ProviderCardScheduling = (
  props: ProviderCardSchedulingProps,
): ReactElement => {
  const { provider, location, startDate } = props;
  const orgContext = useOrgContext();
  const {
    config: { decisionTree },
  } = orgContext;
  const { featureFlags } = useFeatureFlags();
  const { myChartUrl } = useMyChartRedirect(provider);
  const { resetAppointment } = useAppointment();
  const { setIsOpen: setIsModalOpen } = useDecisionTree();

  const isDesktop = useIsDesktop();
  const analytics = useAnalytics();
  const phoneNumber = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const { cachedSlots } = useCachedSlots();

  const nearestAvailability = useMemo(() => {
    if (!cachedSlots) return null;

    let data: NearestAvailability | null = null;

    if (isDesktop) {
      data = getNearestAvailability(cachedSlots);
    } else if (startDate) {
      data = getNearestAvailability(
        filterDatesByFirstStartDate(cachedSlots, startDate),
      );
    }

    return data;
  }, [cachedSlots, isDesktop, startDate]);

  const openScheduling = (
    slot: Partial<NearestSlot> | null,
    showCalendarByDefault: boolean,
  ) => {
    if (
      isSchedulingMyChart({
        provider,
        featureFlags,
        specialtyMap: decisionTree.specialtyMap,
      })
    ) {
      // MyChart is not configured for this directory — no redirect to report
      // and nothing to open, so the action is a no-op.
      if (!myChartUrl) {
        return;
      }

      analytics.ehrRedirectEvent({
        linkContextPage: 'SearchResults',
        linkContextComponent: 'ProviderCard',
        provider,
        phoneNumber,
        location,
        visitTypeId: myChartUrl.searchParams.get('vt') ?? '',
        schedulingFlowType: getSchedulingFlowType(),
        redirectUrl: myChartUrl.toString(),
      });
      window.open(myChartUrl);

      return;
    }

    analytics.openedFullModalEvent({
      linkContextPage: 'SearchResults',
      linkContextComponent: 'ProviderCard',
      provider,
      phoneNumber,
      schedulingFlowType: getSchedulingFlowType(),
    });
    resetAppointment({
      provider,
      departmentId: slot?.locationId,
      slotDate: slot?.date,
      slotDateMode: showCalendarByDefault
        ? SlotDateMode.enum.nearest
        : SlotDateMode.enum.selected,
      showCalendarByDefault,
      // Clear only the slot-specific fields, preserve reasonForVisit
      slotTime: null,
    });
    setIsModalOpen(true);
  };

  return (
    <div className={'mt-6 w-full tablet:mt-[32px] desktop:mt-0'}>
      <div className={'hidden h-full w-full desktop:flex'}>
        <SchedulingDesktop
          {...props}
          openScheduling={openScheduling}
          nearestAvailability={nearestAvailability}
        />
      </div>
      <div className={'w-full desktop:hidden'}>
        <SchedulingMobile
          {...props}
          openScheduling={openScheduling}
          nearestAvailability={nearestAvailability}
        />
      </div>
    </div>
  );
};
